import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Radio from '@mui/joy/Radio';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import { LineChart } from '@mui/x-charts/LineChart';

import { statsAPIService } from '~/api/stats';
import { getLoggedInUser } from '~/store/reducers/account/selector';
import { COLORS } from '~/styles/colors';
import { convertDate, isMobileResolution } from '~/utils/common';
import { useThousandFormatter } from '~/utils/useThousandFormatter';

const mockDate = {
  day: [
    { day: 0, total: 10, unique: 20 },
    { day: 10, total: 80, unique: 100 },
    { day: 15, total: 50, unique: 30 },
    { day: 30, total: 90, unique: 140 },
    { day: 45, total: 24, unique: 40 },
    { day: 50, total: 50, unique: 70 },
  ],
  month: [
    { month: 0, total: 10, unique: 20 },
    { month: 2, total: 80, unique: 100 },
    { month: 4, total: 50, unique: 30 },
    { month: 6, total: 90, unique: 140 },
    { month: 8, total: 24, unique: 40 },
    { month: 12, total: 50, unique: 70 },
  ],
  year: [
    { year: 2010, total: 10, unique: 20 },
    { year: 2014, total: 80, unique: 100 },
    { year: 2017, total: 50, unique: 30 },
    { year: 2019, total: 90, unique: 140 },
    { year: 2021, total: 24, unique: 40 },
    { year: 2024, total: 50, unique: 70 },
  ],
};

type GraphTemplateProps = {
  qrId: number;
};
function GraphTemplate({ qrId }: GraphTemplateProps) {
  const { t } = useTranslation();

  const [currentTab, setCurrentTab] = useState('day');
  const [_currentData, setCurrentData] = useState(mockDate[`${currentTab}`]);
  const [graphData, setGraphData] = useState(null);
  const [isTotal, setTotal] = useState(true);
  const [isUnique, setUnique] = useState(false);
  const [isVisits, setVisits] = useState(false);
  const { data } = useSelector(getLoggedInUser);
  const todayDate = new Date();
  const tommorowDate = new Date(todayDate);
  tommorowDate.setDate(todayDate.getDate() + 1);
  const startOfMonth = new Date(todayDate.getFullYear(), todayDate.getMonth(), 1);
  const endOfMonth = new Date(todayDate.getFullYear(), todayDate.getMonth() + 1, 0);
  const startOfYear = new Date(todayDate.getFullYear(), 0, 1);
  const endOfYear = new Date(todayDate.getFullYear(), 11, 31);

  const getDataGraph = () => {
    if (currentTab === 'day') {
      return graphData?.diagram.date.map((item, index) => {
        return {
          hour: index,
          total: graphData?.diagram.scansCount[index],
          unique: graphData?.diagram.distinctScansCount[index],
          visits: graphData?.diagram.visitsCount[index],
          hoursValue: item,
        };
      });
    } else if (currentTab === 'month') {
      const data = graphData?.diagram.date.map((item, index) => {
        return {
          day: index,
          total: graphData?.diagram.scansCount[index],
          unique: graphData?.diagram.distinctScansCount[index],
          visits: graphData?.diagram.visitsCount[index],
        };
      });
      return data;
    } else if (currentTab === 'year') {
      const data = graphData?.diagram.date.map((item, index) => {
        return {
          month: index,
          total: graphData?.diagram.scansCount[index],
          unique: graphData?.diagram.distinctScansCount[index],
          visits: graphData?.diagram.visitsCount[index],
        };
      });
      return data;
    } else return [];
  };

  const getDateFormatt = (index, dateFormat) => {
    const data = graphData?.diagram.date[index];
    if (!data) return '0';
    if (currentTab === 'day') {
      let formattedValue = data?.split(' ')[1] || '';
      const hasAM = data.includes('AM');
      const hasPM = data.includes('PM');
      if (hasAM) {
        formattedValue += ' AM';
      }
      if (hasPM) {
        formattedValue += ' PM';
      }
      return formattedValue.toString();
    } else if (currentTab === 'month') {
      return dateFormat === 'MM-DD-YYYY' ? data.split('-')[1] : data.split('-')[0];
    } else if (currentTab === 'year') {
      return dateFormat === 'MM-DD-YYYY' ? data.split('-')[0] : data.split('-')[1];
    } else return '0';
  };

  const getPeriodByType = () => {
    if (currentTab === 'day') {
      return {
        gte: convertDate(new Date()),
        lt: convertDate(tommorowDate),
      };
    } else if (currentTab === 'month') {
      return {
        gte: convertDate(startOfMonth),
        lt: convertDate(endOfMonth),
      };
    } else if (currentTab === 'year') {
      return {
        gte: convertDate(startOfYear),
        lt: convertDate(endOfYear),
      };
    }
  };

  const getDataKey = () => {
    if (currentTab === 'day') {
      return 'hour';
    } else if (currentTab === 'month') {
      return 'day';
    } else if (currentTab === 'year') {
      return 'month';
    }
  };

  const loadGraphData = async () => {
    if (qrId) {
      const response = await statsAPIService.getStatsQrCode({
        grouping: currentTab,
        qr_code__id: qrId,
        scanned_at__gte: getPeriodByType().gte,
        scanned_at__lt: getPeriodByType().lt,
      });
      setGraphData(response.data);
    }
  };

  useEffect(() => {
    loadGraphData();
  }, [currentTab]);

  const handleTabPress = (_, newValue) => {
    setCurrentTab(newValue);
    setCurrentData(mockDate[`${newValue}`]);
  };

  const formatNumber = useThousandFormatter();

  const getSeriesByTypes = () => {
    let initArray = [];

    if (isTotal) {
      initArray = [
        ...initArray,
        {
          dataKey: 'total',
          curve: 'linear',
          color: COLORS.purple,
          valueFormatter: formatNumber,
        },
      ];
    }
    if (isUnique) {
      initArray = [
        ...initArray,
        {
          dataKey: 'unique',
          curve: 'linear',
          color: COLORS.graphSecond,
          valueFormatter: formatNumber,
        },
      ];
    }
    if (isVisits) {
      initArray = [
        ...initArray,
        {
          dataKey: 'visits',
          curve: 'linear',
          color: COLORS.orange,
          valueFormatter: formatNumber,
        },
      ];
    }
    return initArray;
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={isMobileResolution ? 'flex-start' : 'center'}
        flexDirection={isMobileResolution ? 'column-reverse' : 'row'}
      >
        <TabContext value={currentTab}>
          <TabList onChange={handleTabPress}>
            <Tab label={t('qrDetail:lastHours')} value="day" />
            <Tab label={t('qrDetail:lastDays')} value="month" />
            <Tab label={t('qrDetail:lastMonth')} value="year" />
          </TabList>
        </TabContext>

        <Box sx={{ mb: isMobileResolution ? 2 : 0 }}>
          <Radio
            color="primary"
            orientation="horizontal"
            size="sm"
            variant="outlined"
            label={t('qrDetail:total')}
            sx={{
              pr: 2,
              '& .MuiRadio-root': {
                color: COLORS.purple,
              },
              '& .Mui-checked': {
                color: COLORS.purple,
                borderColor: COLORS.purple,
              },
            }}
            value="total"
            onClick={() => setTotal((prev) => !prev)}
            checked={isTotal}
          />
          <Radio
            orientation="horizontal"
            size="sm"
            variant="outlined"
            label={t('qrDetail:unique')}
            sx={{
              pr: 2,
              '& .MuiRadio-root': {
                color: COLORS.graphSecond,
              },
              '& .Mui-checked': {
                color: COLORS.graphSecond,
                borderColor: COLORS.graphSecond,
              },
            }}
            value="unique"
            onClick={() => setUnique((prev) => !prev)}
            checked={isUnique}
          />
          <Radio
            color="primary"
            orientation="horizontal"
            size="sm"
            variant="outlined"
            sx={{
              pr: 2,
              '& .MuiRadio-root': {
                color: COLORS.orange,
              },
              '& .Mui-checked': {
                color: COLORS.orange,
                borderColor: COLORS.orange,
              },
            }}
            label={t('qrDetail:visits')}
            onClick={() => setVisits((prev) => !prev)}
            checked={isVisits}
          />
        </Box>
      </Box>

      <LineChart
        xAxis={[
          {
            dataKey: getDataKey(),
            tickNumber: getDataGraph() ? getDataGraph().length : 0,
            valueFormatter: (value) => {
              return getDateFormatt(value, data.dateFormat);
            },
          },
        ]}
        type="line"
        dataset={getDataGraph() || []}
        series={getSeriesByTypes()}
        height={300}
        margin={{ left: 30, right: 30 }}
        sx={{ padding: 0 }}
      />
    </Box>
  );
}

GraphTemplate.propTypes = {
  // text: PropTypes.string,
};

export default GraphTemplate;
